import { useState, useEffect, useRef, useCallback } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useHistory } from 'react-router-dom';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import Loading from '@/components/loading/Loading';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { useEntitlements } from '@/context_providers/user_context/UserProvider';
import EntitlementLockOverlay from '@/components/entitlements/EntitlementLockOverlay';
import EmergencyResponseRouteBar from './EmergencyResponseRouteBar';
import EmergencyResponseRoute from './EmergencyResponderRoute';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { useBannerHeight } from '@/context_providers/banner_context/BannerHeightContext';
import { Routes } from '@/enums/Routes';
import {
  getAllGroupEmergencyContacts,
  getOrgEmergencyContacts,
} from '@/repository/OrganizationDAO';
import {
  GroupEmergencyContactsOnAzure,
  OrgEmergencyContactsOnAzure,
} from '../../../../generated-client/models';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';

// Matches the fixed-width permanent Drawer rendered by EmergencyResponseRouteBar.
const DRAWER_WIDTH = 240;

const EmergencyResponseManagement = () => {
  const [activeGroup, setActiveGroup] = useState<string>('');
  const [emergencyContacts, setEmergencyContacts] = useState<
    GroupEmergencyContactsOnAzure[]
  >([]);
  const [orgEmergencyContacts, setOrgEmergencyContacts] =
    useState<OrgEmergencyContactsOnAzure | null>(null);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const { groups, organization } = useOrganizationUpdaterProvider();
  const entitlements = useEntitlements();
  const { defaultPageBoxPadding } = useBannerHeight();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const history = useHistory();
  const hasInitialized = useRef(false);

  // Load all emergency contacts when organization is available
  useEffect(() => {
    const loadEmergencyContacts = async () => {
      if (!organization?.org_code) return;

      setLoadingContacts(true);
      try {
        const [groupContacts, orgContacts] = await Promise.all([
          getAllGroupEmergencyContacts(organization.org_code),
          getOrgEmergencyContacts(),
        ]);
        setEmergencyContacts(groupContacts);
        setOrgEmergencyContacts(orgContacts);
      } catch (error) {
        console.error('Error loading emergency contacts:', error);
        setEmergencyContacts([]);
        setOrgEmergencyContacts(null);
      } finally {
        setLoadingContacts(false);
      }
    };

    loadEmergencyContacts();
  }, [organization?.org_code]);

  // Navigate to the organization view on initial load
  useEffect(() => {
    if (organization && !hasInitialized.current) {
      hasInitialized.current = true;
      setActiveGroup('organization');
      history.replace(`${Routes.EMERGENCY_RESPONDER_MANAGEMENT}/organization`);
    }
  }, [organization, history]);

  // Callback to update a specific group's emergency contacts after CRUD operations
  const updateGroupContacts = useCallback(
    (updatedContacts: GroupEmergencyContactsOnAzure) => {
      setEmergencyContacts((prev) => {
        const index = prev.findIndex(
          (c) => c.group_id === updatedContacts.group_id,
        );
        if (index >= 0) {
          // Update existing
          const newContacts = [...prev];
          newContacts[index] = updatedContacts;
          return newContacts;
        } else {
          // Add new
          return [...prev, updatedContacts];
        }
      });
    },
    [],
  );

  // Get contacts for a specific group
  const getContactsForGroup = useCallback(
    (groupId: string): GroupEmergencyContactsOnAzure | undefined => {
      return emergencyContacts.find((c) => c.group_id === groupId);
    },
    [emergencyContacts],
  );

  // Callback to update organization emergency contacts after CRUD operations
  const updateOrgEmergencyContacts = useCallback(
    (updatedContacts: OrgEmergencyContactsOnAzure) => {
      setOrgEmergencyContacts(updatedContacts);
    },
    [],
  );

  // Get organization emergency contacts
  const getOrgEmergencyContactsState =
    useCallback((): OrgEmergencyContactsOnAzure | null => {
      return orgEmergencyContacts;
    }, [orgEmergencyContacts]);

  if (!organization || !groups || loadingContacts) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <CssBaseline />
      {/* The sidebar's permanent Drawer is `position: fixed` against the viewport,
          so it must stay outside EntitlementLockOverlay (see that component's
          top-of-file note) — otherwise the lock's blur `filter` would hijack it as
          its containing block and blow the whole page out into a scrollbar. This
          also matches CompassConfigurationsPage, where ConfigurationsRouteBar sits
          outside the overlay for the same reason. */}
      <EmergencyResponseRouteBar
        allGroups={groups}
        setActiveGroup={setActiveGroup}
        activeGroup={activeGroup}
        emergencyContacts={emergencyContacts}
        orgEmergencyContacts={orgEmergencyContacts}
      />
      {/* EntitlementLockOverlay's root relies on `height: 100%` from an ancestor —
          without a definite height here, the overlay's `position: absolute; inset: 0`
          pane collapses to the content's auto height instead of covering the page.
          `defaultPageBoxPadding` (not a fixed 65px navbar offset) accounts for the
          dev/emergency banners stacking above the navbar — a fixed offset would be
          too tall whenever a banner is showing, overflowing the viewport and forcing
          a page-wide scroll. `pl` on desktop excludes the fixed sidebar's width from
          the overlay's centering box, so the lock/description center in the content
          area to the right of the sidebar. Mirrors CompassConfigurationsPage exactly. */}
      <Box
        sx={{
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          ...(isMobile
            ? {
                height: (theme) =>
                  `calc(100dvh - ${theme.spacing(defaultPageBoxPadding)})`,
              }
            : {
                pl: `${DRAWER_WIDTH}px`,
                height: (theme) =>
                  `calc(100dvh - ${theme.spacing(defaultPageBoxPadding)})`,
              }),
        }}
      >
        <EntitlementLockOverlay
          locked={!entitlements.orgHasRespondInventory}
          description={t(StringKeys.RESPONDER_MANAGEMENT_REQUIRES_RESPOND)}
        >
          <EmergencyResponseRoute
            getContactsForGroup={getContactsForGroup}
            updateGroupContacts={updateGroupContacts}
            getOrgEmergencyContacts={getOrgEmergencyContactsState}
            updateOrgEmergencyContacts={updateOrgEmergencyContacts}
          />
        </EntitlementLockOverlay>
      </Box>
    </>
  );
};

const LoadingComponent = () => {
  const { t } = useTranslation();
  return <Loading loadingText={t(StringKeys.LOADING)} />;
};

const EmergencyResponseManagementWithAuth = withAuthenticationRequired(
  EmergencyResponseManagement,
  {
    onRedirecting: LoadingComponent,
  },
);

export default EmergencyResponseManagementWithAuth;
